import { useEffect, useState } from 'react';
import { Notice } from '../labour/shared';
import harvestService from '../../services/harvest';
import { errorMessage } from '../../utils/apiResponse';
import { SUGGESTION_BASIS, type YieldHistory } from '../../types/harvest';
import { num } from './insightsUtils';

/**
 * Bins and t/ha per cultivar area per season, with tree age, and the crop
 * estimate that history suggests for next season. Below it, t/ha by cultivar
 * and tree age across the business — the curve a young block is heading along.
 */
export default function YieldHistoryPanel({ farmId, seasonName }: { farmId: number | null; seasonName: (y: number) => string }) {
  const [data, setData] = useState<YieldHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    harvestService.getYieldHistory(farmId)
      .then(d => { if (live) { setData(d); setError(null); } })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load yield history')); });
    return () => { live = false; };
  }, [farmId]);

  if (!data) return <div className="text-sm text-neutral-500 py-6 text-center">{error ?? 'Loading…'}</div>;
  if (!data.areas.length) return <p className="text-sm text-neutral-500 py-6 text-center">No bins recorded yet. Yield history builds up season by season.</p>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-neutral-500">
        Tonnes are packed-out weight where a packout was entered, bin estimates elsewhere. Tree age is the harvest year less the planting year.
      </p>
      <div className="overflow-x-auto border border-neutral-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Area</th>
              {data.seasons.map(y => <th key={y} className="text-right px-3 py-2 font-medium whitespace-nowrap">{seasonName(y)}</th>)}
              <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Suggested next</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {data.areas.map(a => (
              <tr key={a.cultivar_area_id}>
                <td className="px-3 py-2 min-w-[11rem]">
                  <p className="text-neutral-900">{a.cultivar_area_name}</p>
                  <p className="text-xs text-neutral-500">{[a.block_name, a.cultivar_name, a.area_hectares ? `${num(a.area_hectares)} ha` : null].filter(Boolean).join(' · ')}</p>
                </td>
                {data.seasons.map(y => {
                  const s = a.seasons.find(x => x.season_year === y);
                  return (
                    <td key={y} className="px-3 py-2 text-right whitespace-nowrap align-top">
                      {s ? (
                        <>
                          {num(s.bins)} bins
                          <span className="block text-xs text-neutral-500">{s.tonnes_per_ha != null ? `${num(s.tonnes_per_ha)} t/ha` : `${num(s.tonnes, 2)} t`}{s.tree_age != null ? ` · ${s.tree_age} y` : ''}</span>
                        </>
                      ) : <span className="text-neutral-300">—</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right whitespace-nowrap align-top" title={a.basis ? SUGGESTION_BASIS[a.basis] : undefined}>
                  {a.suggested_bins != null ? `${a.suggested_bins} bins` : '—'}
                  {a.basis && <span className="block text-xs text-neutral-500">{a.basis === 'young_trend' ? 'young trees' : a.basis === 'average' ? '3-season avg' : 'last season'}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.by_cultivar_age.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-semibold text-neutral-900">t/ha by cultivar and tree age</h3>
          <div className="overflow-x-auto border border-neutral-200 rounded-lg max-w-xl">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Cultivar</th>
                  <th className="text-right px-3 py-2 font-medium">Tree age</th>
                  <th className="text-right px-3 py-2 font-medium">t/ha</th>
                  <th className="text-right px-3 py-2 font-medium">Area-seasons</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.by_cultivar_age.map(c => (
                  <tr key={`${c.cultivar_id}:${c.tree_age}`}>
                    <td className="px-3 py-2 text-neutral-900">{c.cultivar_name ?? 'Unknown'}</td>
                    <td className="px-3 py-2 text-right">{c.tree_age} y</td>
                    <td className="px-3 py-2 text-right">{num(c.tonnes_per_ha)}</td>
                    <td className="px-3 py-2 text-right">{c.seasons}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {error && <Notice tone="error">{error}</Notice>}
    </div>
  );
}
